import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogIn, LayoutGrid, Sparkles, ChevronDown, ArrowRight } from "lucide-react";
import { cn } from "../../src/lib/utils";
import { beginBlogSignup, blogSignupHref } from '../../services/blogConversion';
import { Magnetic } from "./Magnetic";
import { LanguageSelector } from "./LanguageSelector";
import { FEATURE_PAGES, FEATURES_HUB_PATH, featurePath } from "../../src/content/featurePages";
import { featureIcon } from "../features/featureIcons";

interface NavbarProps {
  onAuth?: () => void;
  transparent?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({ onAuth, transparent = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const isBlog = location.pathname === '/blog' || location.pathname.startsWith('/blog/');
  const isFeaturesPage = location.pathname === FEATURES_HUB_PATH || location.pathname.startsWith(`${FEATURES_HUB_PATH}/`);

  // Close menus on navigation; Escape closes the features dropdown.
  useEffect(() => { setFeaturesOpen(false); setIsOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!featuresOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFeaturesOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [featuresOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Every item is its own page. Features (a dropdown of the feature pages)
  // renders first; Pricing and Blog follow.
  const navItems: { to: string; i18nKey: string }[] = [
    { to: '/pricing', i18nKey: 'landing.pricing' },
    { to: '/blog', i18nKey: 'nav.blog' },
  ];
  const isActive = (to: string) => location.pathname === to || location.pathname.startsWith(`${to}/`);
  const navLinkClass = (active: boolean) => cn(
    "text-[10px] font-black uppercase tracking-wider cursor-pointer transition-all",
    active ? "text-[#10B981]" : "text-white/40 hover:text-[#10B981]"
  );

  const featuresDropdown = (
    <div className="relative" onMouseEnter={() => setFeaturesOpen(true)} onMouseLeave={() => setFeaturesOpen(false)}>
      <div className="flex items-center gap-1">
        <Link to={FEATURES_HUB_PATH} className={navLinkClass(isFeaturesPage || featuresOpen)} onFocus={() => setFeaturesOpen(true)}>
          {t('landing.features_title')}
        </Link>
        <button type="button" onClick={() => setFeaturesOpen(o => !o)} aria-expanded={featuresOpen} aria-haspopup="menu"
          aria-label={t('landing.features_title')}
          className={cn("p-0.5 transition-all", isFeaturesPage || featuresOpen ? "text-[#10B981]" : "text-white/40 hover:text-[#10B981]")}>
          <ChevronDown size={12} className={cn("transition-transform", featuresOpen && "rotate-180")} />
        </button>
      </div>
      <AnimatePresence>
        {featuresOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-5 w-[440px]"
          >
            <div role="menu" className="rounded-[2rem] bg-[#0A0F1E]/98 backdrop-blur-2xl border border-white/10 shadow-2xl p-3">
              {FEATURE_PAGES.map(page => {
                const Icon = featureIcon(page.icon);
                const active = location.pathname === featurePath(page.slug);
                return (
                  <Link key={page.slug} role="menuitem" to={featurePath(page.slug)}
                    className={cn("flex items-start gap-4 p-3.5 rounded-2xl transition-colors", active ? "bg-[#10B981]/10" : "hover:bg-white/5")}>
                    <span className="w-9 h-9 shrink-0 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center"><Icon size={17} /></span>
                    <span className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-[11px] font-black uppercase tracking-wider text-white">{t(`features_pages.${page.key}.card_title`)}</span>
                      <span className="text-xs font-medium text-white/45 leading-snug normal-case tracking-normal">{t(`features_pages.${page.key}.card_desc`)}</span>
                    </span>
                  </Link>
                );
              })}
              <Link to={FEATURES_HUB_PATH} role="menuitem" className="mt-1 flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border-t border-white/5 text-[10px] font-black uppercase tracking-widest text-white/50 hover:text-[#10B981] transition-colors">
                {t('features_pages.common.all_features', { defaultValue: 'All features' })} <ArrowRight size={13} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <nav className="fixed top-4 sm:top-8 left-0 right-0 mx-auto z-[1000] w-[95%] sm:w-[92%] max-w-5xl transition-all duration-500 font-outfit">
      <div 
        className={cn(
          "px-4 sm:px-8 py-3 sm:py-4 rounded-3xl sm:rounded-full border border-white/5 flex items-center justify-between transition-all duration-500",
          scrolled || !transparent || isOpen
            ? "bg-[#0A0F1E]/95 backdrop-blur-2xl shadow-2xl transform scale-[0.98]" 
            : "bg-white/5 backdrop-blur-md"
        )}
      >
        <Link to="/" className={cn("flex items-center group cursor-pointer transition-transform transform hover:scale-105 active:scale-95 duration-500", isBlog ? "gap-2 sm:gap-3.5" : "gap-3.5")}>
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-white flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.3)] overflow-hidden border border-gray-50 group-hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] transition-all duration-500">
            <img loading="lazy" src="/icon-96x96.png" alt="CartLens Logo" className="w-full h-full object-contain p-1" />
          </div>
          <span className={cn("font-outfit font-black sm:text-2xl tracking-tighter uppercase leading-none text-white", isBlog ? "text-base" : "text-xl")}>CartLens</span>
        </Link>

        <div className="hidden lg:flex items-center gap-10">
          {featuresDropdown}
          {navItems.map(item => (
            <Link key={item.to} to={item.to} className={navLinkClass(isActive(item.to))} aria-current={isActive(item.to) ? 'page' : undefined}>
              {t(item.i18nKey)}
            </Link>
          ))}
        </div>

        <div className={cn("flex items-center sm:gap-5", isBlog ? "gap-2" : "gap-3")}>
          <LanguageSelector variant="dark" className={isBlog ? "hidden sm:block" : undefined} />
          {isBlog ? (
            <Link to={blogSignupHref(location.pathname, 'navbar')} onClick={() => beginBlogSignup(location.pathname, 'navbar')}
              className="max-w-32 sm:max-w-none rounded-full bg-[#10B981] px-3 sm:px-7 py-3 text-center text-[10px] sm:text-[11px] font-black text-[#050810] hover:bg-[#34D399]">
              {t('auth.create_account', { defaultValue: 'Create Account' })}
            </Link>
          ) : <Magnetic>
            <button 
              onClick={onAuth ?? undefined}
              className="hidden sm:block px-7 py-3 rounded-full bg-[#10B981] text-[#050810] font-black text-[11px] uppercase tracking-wider transform hover:scale-105 active:scale-95 transition-all shadow-xl"
            >
              {t('landing.get_started')}
            </button>
          </Magnetic>}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="lg:hidden w-10 h-10 rounded-2xl bg-white/5 flex items-center justify-center text-white"
            aria-label={t('blog_conversion.toggle_menu', { defaultValue: 'Toggle menu' })}
          >
            {isOpen ? <LogIn size={20} /> : <LayoutGrid size={20} />}
            <span className="sr-only">{t('blog_conversion.toggle_menu', { defaultValue: 'Toggle menu' })}</span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -20 }} 
            className="absolute top-full left-0 right-0 mt-5 p-10 rounded-[3rem] bg-[#0A0F1E]/98 backdrop-blur-3xl border border-white/5 shadow-2xl lg:hidden flex flex-col gap-8 max-h-[calc(100dvh-7rem)] overflow-y-auto"
          >
            <div className="flex flex-col gap-4">
              <Link to={FEATURES_HUB_PATH} onClick={() => setIsOpen(false)}
                className={cn("text-xl font-black uppercase tracking-wide transition-all", location.pathname === FEATURES_HUB_PATH ? "text-[#10B981]" : "text-white/60")}>
                {t('landing.features_title')}
              </Link>
              <div className="flex flex-col gap-3 pl-4 border-l border-white/10">
                {FEATURE_PAGES.map(page => {
                  const Icon = featureIcon(page.icon);
                  const active = location.pathname === featurePath(page.slug);
                  return (
                    <Link key={page.slug} to={featurePath(page.slug)} onClick={() => setIsOpen(false)}
                      className={cn("flex items-center gap-3 text-sm font-black uppercase tracking-wider transition-colors", active ? "text-[#10B981]" : "text-white/45 hover:text-white")}>
                      <Icon size={15} className="shrink-0" /> {t(`features_pages.${page.key}.card_title`)}
                    </Link>
                  );
                })}
              </div>
            </div>
            {navItems.map(item => (
              <Link key={item.to} to={item.to} onClick={() => setIsOpen(false)}
                className={cn("text-xl font-black uppercase tracking-wide transition-all", isActive(item.to) ? "text-[#10B981]" : "text-white/60")}>
                {t(item.i18nKey)}
              </Link>
            ))}
            {isBlog && <LanguageSelector variant="dark" className="sm:hidden" />}
            {isBlog ? (
              <Link to={blogSignupHref(location.pathname, 'menu')}
                onClick={() => { beginBlogSignup(location.pathname, 'menu'); setIsOpen(false); }}
                className="w-full py-5 rounded-full bg-[#10B981] text-[#050810] text-center font-black text-sm">
                {t('auth.create_account', { defaultValue: 'Create Account' })}
              </Link>
            ) : <button
              onClick={() => { onAuth?.(); setIsOpen(false); }}
              className="w-full py-6 rounded-2xl bg-[#10B981] text-[#050810] font-black uppercase text-sm tracking-wider"
            >
              {t('landing.get_started')}
            </button>}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
